package com.essntl.core.whatsapp.data

import com.essntl.config.EssntlConfig
import com.essntl.core.network.IO
import com.essntl.core.utils.error.BadRequestException
import com.essntl.core.whatsapp.data.response.*
import com.essntl.core.whatsapp.domain.WhatsappClient
import com.essntl.core.whatsapp.domain.models.*
import com.essntl.features.message.domain.model.WhapiChatModel
import com.essntl.features.message.domain.model.WhapiMessageModel
import io.ktor.client.*
import io.ktor.client.call.*
import io.ktor.client.request.*
import io.ktor.client.statement.*
import io.ktor.http.*
import kotlinx.coroutines.Dispatchers
import kotlinx.coroutines.withContext

class WhatsappClientImpl(
    private val httpClient: HttpClient,
) : WhatsappClient {
    override suspend fun sendMessage(template: WhatsappTemplateData): Result<Unit> =
        runCatching {
            withContext(Dispatchers.IO) {
                val response = httpClient.post(BASE_URL + "/${EssntlConfig.whatsappPhoneNumberId}/messages") {
                    header(HttpHeaders.ContentType, ContentType.Application.Json)
                    bearerAuth(EssntlConfig.whatsappAuthToken)
                    setBody(template)
                }

                if (!response.status.isSuccess())
                    throw BadRequestException()
            }
        }

    override suspend fun sendWhatsappMessage(template: WhatsappTextMessageData): Result<WhatsappResponse> =
        runCatching {
            withContext(Dispatchers.IO) {
                val response = httpClient.post(BASE_URL + "/${EssntlConfig.whatsappPhoneNumberId}/messages") {
                    header(HttpHeaders.ContentType, ContentType.Application.Json)
                    bearerAuth(EssntlConfig.whatsappAuthToken)
                    setBody(template)
                }
                if (response.status.isSuccess())
                    response.body<WhatsappResponseDto>().toModel()
                else {
                    println("WhatsappError: ${response.bodyAsText()}")
                    throw BadRequestException()
                }
            }
        }

    override suspend fun getWhapiChats(page: Long, count: Int): Result<List<WhapiChatModel>> =
        runCatching {
            withContext(Dispatchers.IO) {
                val offset = page * count
                val response = httpClient.get("$WHAPI_BASE_URL/chats?offset=$offset&count=${count}") {
                    bearerAuth(EssntlConfig.whapiAuthToken)
                }
                if (response.status.isSuccess())
                    response.body<WhapiChatsResponseDto>().chats.filter { it.source != "system" }
                        .map { it.toModel() }
                else
                    throw BadRequestException()
            }
        }

    override suspend fun getWhapiChatMessages(chatId: String): Result<List<WhapiMessageModel>> =
        runCatching {
            withContext(Dispatchers.IO) {
                val response = httpClient.get("$WHAPI_BASE_URL/messages/list/$chatId") {
                    bearerAuth(EssntlConfig.whapiAuthToken)
                }
                if (response.status.isSuccess())
                    response.body<WhapiMessagesResponseDto>().messages.map { it.toModel() }
                else
                    throw BadRequestException()
            }
        }

    override suspend fun getWhapiChatById(chatId: String): Result<WhapiChatModel> =
        runCatching {
            withContext(Dispatchers.IO) {
                val response = httpClient.get("$WHAPI_BASE_URL/chats/$chatId") {
                    bearerAuth(EssntlConfig.whapiAuthToken)
                }
                if (response.status.isSuccess())
                    response.body<WhapiChatDto>().toModel()
                else
                    throw BadRequestException()
            }
        }

    override suspend fun sendWhapiTextMessages(message: WhapiTextMessageTemplate): Result<WhapiMessageModel> =
        runCatching {
            withContext(Dispatchers.IO) {
                val response = httpClient.post("$WHAPI_BASE_URL/messages/text") {
                    header(HttpHeaders.ContentType, ContentType.Application.Json)
                    bearerAuth(EssntlConfig.whapiAuthToken)
                    setBody(message)
                }
                if (response.status.isSuccess())
                    response.body<WhapiSendMessageResponseDto>().message.toModel()
                else
                    throw BadRequestException()
            }
        }

    override suspend fun updateWhapiChat(chatId: String, chatTemplate: WhapiChatTemplate): Result<Unit> =
        runCatching {
            withContext(Dispatchers.IO) {
                val response = httpClient.patch("$WHAPI_BASE_URL/chats/$chatId") {
                    header(HttpHeaders.ContentType, ContentType.Application.Json)
                    bearerAuth(EssntlConfig.whapiAuthToken)
                    setBody(chatTemplate)
                }
                if (!response.status.isSuccess())
                    throw BadRequestException()
            }
        }

    override suspend fun createWhapiGroup(groupTemplate: WhapiGroupTemplate): Result<WhapiChatModel> =
        runCatching {
            withContext(Dispatchers.IO) {
                val response = httpClient.post("$WHAPI_BASE_URL/groups") {
                    header(HttpHeaders.ContentType, ContentType.Application.Json)
                    bearerAuth(EssntlConfig.whapiAuthToken)
                    setBody(groupTemplate)
                }
                if (response.status.isSuccess())
                    response.body<WhapiChatDto>().toModel()
                else
                    throw BadRequestException()
            }
        }

    override suspend fun getWhapiGroups(): Result<List<WhapiChatModel>> =
        runCatching {
            withContext(Dispatchers.IO) {
                val response = httpClient.get("$WHAPI_BASE_URL/groups") {
                    bearerAuth(EssntlConfig.whapiAuthToken)
                }
                if (response.status.isSuccess())
                    response.body<WhapiGroupsResponseDto>().groups.map { it.toModel() }
                else
                    throw BadRequestException()
            }
        }

    override suspend fun getWhapiGroupById(groupId: String): Result<WhapiChatModel> =
        runCatching {
            withContext(Dispatchers.IO) {
                val response = httpClient.get("$WHAPI_BASE_URL/groups/$groupId") {
                    bearerAuth(EssntlConfig.whapiAuthToken)
                }
                if (response.status.isSuccess())
                    response.body<WhapiChatDto>().toModel()
                else
                    throw BadRequestException()
            }
        }

    override suspend fun getWhapiMessageById(whapiId: String): Result<WhapiMessageModel> =
        runCatching {
            withContext(Dispatchers.IO) {
                val response = httpClient.get("$WHAPI_BASE_URL/messages/$whapiId") {
                    bearerAuth(EssntlConfig.whapiAuthToken)
                }
                if (response.status.isSuccess())
                    response.body<WhapiMessageDto>().toModel()
                else
                    throw BadRequestException()
            }
        }

    override suspend fun reactToMessage(messageId: String, emoji: String): Result<Unit> =
        runCatching {
            withContext(Dispatchers.IO) {
                val response = httpClient.put("$WHAPI_BASE_URL/messages/$messageId/reaction") {
                    header(HttpHeaders.ContentType, ContentType.Application.Json)
                    bearerAuth(EssntlConfig.whapiAuthToken)
                    setBody(mapOf("emoji" to emoji))
                }
                if (!response.status.isSuccess())
                    throw BadRequestException()
            }
        }

    override suspend fun checkContact(contactId: String): Result<Unit> =
        runCatching {
            withContext(Dispatchers.IO) {
                val response = httpClient.get("$WHAPI_BASE_URL/contacts/$contactId") {
                    bearerAuth(EssntlConfig.whapiAuthToken)
                }

                if (!response.status.isSuccess())
                    throw BadRequestException()
            }
        }

    override suspend fun sendContact(to: String): Result<Unit> =
        runCatching {
            withContext(Dispatchers.IO) {
                val response = httpClient.post("$WHAPI_BASE_URL/contacts/34671023512") {
                    header(HttpHeaders.ContentType, ContentType.Application.Json)
                    bearerAuth(EssntlConfig.whapiAuthToken)
                    setBody(SendContact("${to}@s.whatsapp.net"))
                }

                if (!response.status.isSuccess())
                    throw BadRequestException()
            }
        }

    override suspend fun updateGroupDescription(groupId: String, description: String): Result<Unit> =
        runCatching {
            withContext(Dispatchers.IO) {
                val response = httpClient.put("$WHAPI_BASE_URL/groups/$groupId") {
                    header(HttpHeaders.ContentType, ContentType.Application.Json)
                    bearerAuth(EssntlConfig.whapiAuthToken)
                    setBody(mapOf("description" to description))
                }

                if (!response.status.isSuccess())
                    throw BadRequestException()
            }
        }

    override suspend fun addContactToGroup(groupId: String, participants: Set<String>): Result<Unit> =
        runCatching {
            withContext(Dispatchers.IO) {
                val response = httpClient.post("$WHAPI_BASE_URL/groups/$groupId/participants") {
                    header(HttpHeaders.ContentType, ContentType.Application.Json)
                    bearerAuth(EssntlConfig.whapiAuthToken)
                    setBody(mapOf("participants" to participants))
                }

                if (!response.status.isSuccess())
                    throw BadRequestException()
            }
        }

    override suspend fun removeContactFromGroup(groupId: String, participants: Set<String>): Result<Unit> =
        runCatching {
            withContext(Dispatchers.IO) {
                val response = httpClient.delete("$WHAPI_BASE_URL/groups/$groupId/participants") {
                    header(HttpHeaders.ContentType, ContentType.Application.Json)
                    bearerAuth(EssntlConfig.whapiAuthToken)
                    setBody(mapOf("participants" to participants))
                }

                if (!response.status.isSuccess())
                    throw BadRequestException()
            }
        }

    companion object {
        private const val BASE_URL = "https://graph.facebook.com/v19.0"
        private const val WHAPI_BASE_URL = "https://gate.whapi.cloud"
    }
}